<script>
  // Doc panel for the Status page. Same shape as CaptureHelp -- collapsed
  // by default, copy-whole-doc button up top, sections below.
  import { ui } from '../lib/ui.svelte.js';
  let open = $state(false);

  const fullDoc =
`# Configuring a server for the hook-index Status page

The status checker probes each registered URL on its configured interval and stores up/down state, latency, and a 7-day history. Anything that returns a successful HTTP response from a stable URL works -- there's no agent to install.

## Recommended /health endpoint

Expose an endpoint that returns 200 OK quickly, with no auth. A common shape:

\`\`\`http
GET /health
200 OK
Content-Type: application/json

{"status":"ok","version":"1.2.3","uptime_seconds":12345}
\`\`\`

Rules:
- Status 2xx = healthy. Anything else (or no response within the timeout) = down.
- The body is not inspected; only the status code matters.
- Keep the handler cheap. Avoid touching downstream services from /health unless you specifically want a deep check (and then expect higher latency).
- Don't require auth. The checker connects unauthenticated, with User-Agent \`hook-index-status/1\`.

## Per-target options
- **URL** -- full URL including scheme. http and https both work.
- **Method** -- GET (default), HEAD, POST, etc. HEAD is good when you want to skip the body.
- **Expected status** -- leave blank for "any 2xx". Set to a specific code (e.g. 204) when the endpoint returns a non-2xx as success on purpose.
- **Interval** -- seconds between checks per target. Default 60. Minimum sensible value ~10s; very low values can swamp the target and the checker.
- **Timeout** -- seconds to wait for the response. Default 10. Targets routinely slower than 10s should bump this to 30s or more.

## Alerts on transitions

When a target transitions up->down (or recovers), an email is sent through SMTP2GO -- configured on the **Settings** tab. Set the API key, sender, and recipient list there once and every transition will dispatch automatically.

## What's stored
- Per target: name, URL, method, expected status, interval, timeout, enabled.
- Latest probe (denormalised): up/down, status, latency_ms, error message, checked_at.
- Per-result history (TTL 7 days): each probe's outcome.
`;

  function copy() {
    navigator.clipboard?.writeText(fullDoc);
    ui.snack('Copied', 'success', 1500);
  }
</script>

<section class="status-help bg-surface-container rounded-2xl mb-4 overflow-hidden">
  <button type="button" class="status-help-trigger" onclick={() => (open = !open)} aria-expanded={open}>
    <span class="material-symbols-outlined text-primary">help</span>
    <span class="font-medium">Configuring a server for monitoring</span>
    <span class="material-symbols-outlined text-on-surface-variant status-help-chevron" class:open>expand_more</span>
  </button>

  {#if open}
    <div class="status-help-body">
      <div class="status-help-toolbar">
        <button type="button" class="status-help-copy" onclick={copy} title="Copy entire doc (markdown)">
          <span class="material-symbols-outlined text-base align-middle">content_copy</span>
          Copy whole doc
        </button>
      </div>

      <h3>Recommended <code>/health</code> endpoint</h3>
      <p>
        Expose an endpoint that returns <strong>200 OK</strong> quickly, with no auth. The status checker only looks at the response code; the body is not inspected.
      </p>
      <pre class="status-help-code"><code>{`GET /health
200 OK
Content-Type: application/json

{"status":"ok","version":"1.2.3","uptime_seconds":12345}`}</code></pre>

      <h3>Rules</h3>
      <ul>
        <li>Any 2xx response = healthy. Non-2xx, transport errors, or no response within the timeout = down.</li>
        <li>Keep the handler cheap. Avoid hitting downstream services from <code>/health</code> unless you specifically want a deep check (and then expect higher latency).</li>
        <li>Don't require auth. The checker connects unauthenticated, with <code>User-Agent: hook-index-status/1</code>.</li>
        <li>Don't follow redirects: if your <code>/health</code> 301-redirects to <code>/health/</code>, set the target URL to the final one. The checker stops at the first response (a 301 is recorded as the actual status).</li>
      </ul>

      <h3>Per-target options</h3>
      <ul>
        <li><strong>URL</strong> — full URL including scheme. <code>http</code> and <code>https</code> both work.</li>
        <li><strong>Method</strong> — <code>GET</code> (default), <code>HEAD</code> (skip body), <code>POST</code>, etc.</li>
        <li><strong>Expected status</strong> — blank means "any 2xx". Set to a specific code (e.g. <code>204</code>) for endpoints that succeed with a non-2xx on purpose.</li>
        <li><strong>Interval</strong> — seconds between checks. Default 60. Sensible minimum ~10s.</li>
        <li><strong>Timeout</strong> — per-probe timeout. Default 10s. Bump for targets routinely slower.</li>
      </ul>

      <h3>Alerts</h3>
      <p>
        Up→down (and recovery) transitions email out via <a href="https://www.smtp2go.com/" target="_blank" rel="noopener" class="status-help-link">SMTP2GO</a>. Configure the API key, sender, and recipients on the
        <a href="/settings" class="status-help-link">Settings</a> tab — that's a one-time setup.
      </p>
    </div>
  {/if}
</section>

<style>
  .status-help-trigger {
    inline-size: 100%;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    background: transparent;
    color: inherit;
    border: 0;
    text-align: start;
    cursor: pointer;
  }
  .status-help-trigger:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .status-help-chevron { transition: transform 200ms; margin-inline-start: auto; }
  .status-help-chevron.open { transform: rotate(180deg); }

  .status-help-body {
    padding: 0 1rem 1rem;
    border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  .status-help-toolbar {
    display: flex;
    justify-content: flex-end;
    padding-block-start: 0.75rem;
  }
  .status-help-copy {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.875rem;
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 999px;
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  .status-help-copy:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }

  .status-help-body h3 {
    font-size: 14px;
    font-weight: 500;
    margin-block: 1rem 0.5rem;
  }
  .status-help-body p {
    font-size: 14px;
    line-height: 1.55;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin-block: 0 0.5rem;
  }
  .status-help-body ul {
    margin: 0;
    padding-inline-start: 1.1rem;
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .status-help-body code {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    background: rgb(var(--md-sys-color-on-surface) / 0.06);
    padding: 1px 5px;
    border-radius: 4px;
  }
  .status-help-code {
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    padding: 0.75rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0 0 0.5rem;
    font-size: 12px;
    line-height: 1.6;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  .status-help-link { color: rgb(var(--md-sys-color-primary)); text-decoration: underline; }
</style>
